import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;

  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      resolve(null);
    }, timeoutMs);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ─── Types ───────────────────────────────────────────────────────────

export interface NotificationRule {
  id: number;
  account_id: number;
  formula_id: number | null;
  formula_code?: string;
  formula_name?: string;
  name: string;
  category: string;
  rule_type: string;
  severity: string;
  condition: Record<string, any>;
  channels: { dashboard?: boolean; email?: boolean; whatsapp?: boolean };
  assignees: { all?: boolean; userIds?: string[] };
  enabled: boolean;
  cooldown_hours: number;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: number;
  account_id: number;
  rule_id: number | null;
  formula_id: number | null;
  formula_code?: string;
  formula_name?: string;
  category: string;
  severity: string;
  title: string;
  message: string;
  data: Record<string, any> | null;
  status: string;
  channels_sent: Record<string, any>;
  created_at: string;
  dismissed_at: string | null;
  resolved_at: string | null;
}

// ─── Notification Rules API ──────────────────────────────────────────

export async function fetchNotificationRules(accountId?: string, filters?: {
  enabled?: boolean;
  category?: string;
  formula_code?: string;
}): Promise<NotificationRule[]> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (accountId) params.set('accountId', accountId);
  if (filters?.enabled !== undefined) params.set('enabled', String(filters.enabled));
  if (filters?.category) params.set('category', filters.category);
  if (filters?.formula_code) params.set('formula_code', filters.formula_code);

  const qs = params.toString();
  const url = `${API_URL}/api/v2/watch/rules${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('Failed to fetch notification rules');
  const data = await res.json();
  return data.rules;
}

export async function createNotificationRuleApi(accountId?: string, rule?: {
  formula_code?: string;
  name: string;
  category: string;
  rule_type: string;
  severity?: string;
  condition?: Record<string, any>;
  channels?: Record<string, boolean>;
  assignees?: Record<string, any>;
  enabled?: boolean;
  cooldown_hours?: number;
}): Promise<NotificationRule> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/rules${qs}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(rule),
  });
  if (!res.ok) throw new Error('Failed to create watch rule');
  const data = await res.json();
  return data.rule;
}

export async function updateNotificationRuleApi(
  ruleId: number,
  updates: Partial<Pick<NotificationRule, 'name' | 'severity' | 'condition' | 'channels' | 'assignees' | 'enabled' | 'cooldown_hours'>>,
  accountId?: string,
): Promise<NotificationRule> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/rules/${ruleId}${qs}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('Failed to update watch rule');
  const data = await res.json();
  return data.rule;
}

export async function deleteNotificationRuleApi(ruleId: number, accountId?: string): Promise<void> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/rules/${ruleId}${qs}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error('Failed to delete watch rule');
}

export async function seedNotificationRules(accountId?: string, reset = false): Promise<{ rules: NotificationRule[]; deleted: number; seeded: number }> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/rules/seed${qs}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ reset }),
  });
  if (!res.ok) throw new Error('Failed to seed watch rules');
  const data = await res.json();
  return { rules: data.rules, deleted: data.deleted || 0, seeded: data.seeded || 0 };
}

// ─── Notifications API ───────────────────────────────────────────────

export async function fetchNotifications(accountId?: string, filters?: {
  status?: string;
  category?: string;
  formula_code?: string;
  limit?: number;
}): Promise<Notification[]> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (accountId) params.set('accountId', accountId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.formula_code) params.set('formula_code', filters.formula_code);
  if (filters?.limit) params.set('limit', String(filters.limit));

  const qs = params.toString();
  const url = `${API_URL}/api/v2/watch/notifications${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('Failed to fetch notifications');
  const data = await res.json();
  return data.notifications;
}

export async function fetchNotificationCount(accountId?: string): Promise<number> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/notifications/count${qs}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch notification count');
  const data = await res.json();
  return data.count;
}

export async function dismissNotificationApi(notificationId: number, accountId?: string): Promise<Notification> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/notifications/${notificationId}/dismiss${qs}`, {
    method: 'PATCH',
    headers,
  });
  if (!res.ok) throw new Error('Failed to dismiss notification');
  const data = await res.json();
  return data.notification;
}

export async function clearAllNotifications(accountId?: string): Promise<number> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/notifications/all${qs}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error('Failed to clear notifications');
  const data = await res.json();
  return data.deleted;
}

export interface RuleEvalLog {
  ruleId: number;
  ruleName: string;
  ruleType: string;
  formulaCode: string;
  formulaName: string;
  scope?: 'account-wide' | 'formula-specific';
  verdict: 'fired' | 'passed' | 'cooldown' | 'error' | 'no_evaluator';
  severity: string;
  detail: string;
  data?: Record<string, any>;
}

export interface EvalLogResult {
  notificationsCreated: number;
  durationMs: number;
  log: RuleEvalLog[];
}

export async function triggerEvaluation(accountId?: string): Promise<EvalLogResult> {
  const headers = await getAuthHeaders();
  const qs = accountId ? `?accountId=${accountId}` : '';
  const res = await fetch(`${API_URL}/api/v2/watch/evaluate${qs}`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) throw new Error('Failed to trigger evaluation');
  const data = await res.json();
  return { notificationsCreated: data.notificationsCreated, durationMs: data.durationMs, log: data.log || [] };
}
